import type { Topic } from "@circle-react/types/Topic";
import type {
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { arrayMove } from "@dnd-kit/sortable";
import { useCallback, useState } from "react";

interface UseTopicsDragAndDropProps {
  onReorderTopics: (topics: Topic[]) => void;
  topics: Topic[];
}

export const useTopicsDragAndDrop = ({
  topics,
  onReorderTopics,
}: UseTopicsDragAndDropProps) => {
  const [draggedTopicId, setDraggedTopicId] = useState<UniqueIdentifier | null>(
    null,
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggedTopicId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        onReorderTopics(
          arrayMove(
            topics,
            topics.findIndex(t => t.id === active.id),
            topics.findIndex(t => t.id === over.id),
          ),
        );
      }

      setDraggedTopicId(null);
    },
    [topics, onReorderTopics],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const draggedTopic = topics.find(topic => topic.id === draggedTopicId);

  return {
    draggedTopic,
    handleDragStart,
    handleDragEnd,
    sensors,
  };
};
